/**
 * SUNTRONIX ONE — Production Readiness Control Centre.
 * Single-page scorecard that combines automated probe outcomes with
 * manual attestations and role sign-offs.  Never fabricates a score.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, PlayCircle, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ChevronRight, Zap,
} from "lucide-react";

const OVERALL_STYLE = {
  NOT_READY:              { bg: "#FFF0F2", fg: "#C2333F", label: "Not ready" },
  CONDITIONALLY_READY:    { bg: "#FFF8E7", fg: "#B25A00", label: "Conditionally ready" },
  PILOT_READY:            { bg: "#ECFAFF", fg: "#086F8E", label: "Pilot ready" },
  PRODUCTION_READY:       { bg: "#ECFDF6", fg: "#169C70", label: "Production ready" },
  LIVE:                   { bg: "#EFF5FF", fg: "#246BFD", label: "Live" },
  SUSPENDED:              { bg: "#F2F4F7", fg: "#667085", label: "Suspended" },
};
const STATUS_STYLE = {
  PASSED:                 { bg: "#ECFDF6", fg: "#169C70" },
  PASSED_WITH_EXCEPTION:  { bg: "#FFF8E7", fg: "#B25A00" },
  FAILED:                 { bg: "#FFF0F2", fg: "#C2333F" },
  BLOCKED:                { bg: "#FFF0F2", fg: "#C2333F" },
  RETEST_REQUIRED:        { bg: "#FFF8E7", fg: "#B25A00" },
  IN_PROGRESS:            { bg: "#EFF5FF", fg: "#246BFD" },
  NOT_STARTED:            { bg: "#F2F4F7", fg: "#667085" },
};

export default function ProductionReadiness() {
  const [card, setCard] = useState(null);
  const [tenantId, setTenantId] = useState("");
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [attest, setAttest] = useState(null);   // { check }
  const [signoffOpen, setSignoffOpen] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
      const { data } = await api.get(`/production-readiness/scorecard${params}`);
      setCard(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [tenantId]);

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setTenants(data.items || []))
      .catch(() => {});
    load();
  }, [load]);

  const runAll = async () => {
    setBusy(true);
    try {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
      const { data } = await api.post(`/production-readiness/probe-all${params}`);
      toast.success(`${data.count} automated probes ran`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runProbe = async (check_id) => {
    try {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
      const { data } = await api.post(
        `/production-readiness/probe/${check_id}${params}`);
      toast.success(`${check_id} → ${data.status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const oStyle = card ? OVERALL_STYLE[card.overall] : null;

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="readiness-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-[#667085]">
            Administration · Platform
          </div>
          <h1 className="text-2xl font-heading font-semibold text-[#101828] tracking-tight">
            Production readiness
          </h1>
          <p className="text-[13px] text-[#667085] mt-1 max-w-3xl">
            Verifies that SUNTRONIX ONE is secure, tenant-isolated,
            functionally correct, commercially reconciled and recoverable.
            No release without a passing scorecard.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                  data-testid="readiness-tenant"
                  className="h-9 border border-slate-200 rounded px-2 text-[12px] bg-white">
            <option value="">Global scope</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Button onClick={runAll} disabled={busy}
                    data-testid="readiness-run-all"
                    className="bg-brand-blue hover:bg-brand-blueh">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Zap className="h-4 w-4 mr-1" />}
            Run all automated probes
          </Button>
          <Button variant="outline" onClick={() => setSignoffOpen(true)}
                    data-testid="readiness-signoff-btn">
            Sign off
          </Button>
        </div>
      </div>

      {/* Overall gate */}
      {card && oStyle && (
        <div className="rounded-md border p-4 flex items-center justify-between"
             style={{ background: oStyle.bg, borderColor: oStyle.fg }}
             data-testid="readiness-overall">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-6 w-6" style={{ color: oStyle.fg }} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em]"
                   style={{ color: oStyle.fg }}>Overall gate</div>
              <div className="text-2xl font-semibold" style={{ color: oStyle.fg }}
                   data-testid="readiness-overall-label">
                {oStyle.label}
              </div>
            </div>
          </div>
          <div className="text-right text-[12px]" style={{ color: oStyle.fg }}>
            <div>Open <b>P0</b>: {card.open_p0}</div>
            <div>Open <b>P1</b>: {card.open_p1}</div>
            <div className="text-[11px] opacity-70">
              Scope: {card.scope === "__global__" ? "global" : card.scope}
            </div>
          </div>
        </div>
      )}

      {/* Domain roll-up */}
      {card && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2"
             data-testid="readiness-domains">
          {card.domains.map((d) => {
            const total = d.total || 1;
            const pct = Math.round((d.passed / total) * 100);
            const tone = d.failed > 0 ? "coral" : d.exception > 0 ? "amber"
              : d.passed === total ? "mint" : "blue";
            const bg = { mint: "#ECFDF6", amber: "#FFF8E7",
              coral: "#FFF0F2", blue: "#EFF5FF" }[tone];
            const fg = { mint: "#169C70", amber: "#B25A00",
              coral: "#C2333F", blue: "#246BFD" }[tone];
            return (
              <div key={d.domain}
                   className="rounded-md border border-slate-200 p-3 relative overflow-hidden"
                   style={{ background: bg }}
                   data-testid={`readiness-domain-${d.domain}`}>
                <div className="absolute inset-y-0 left-0 w-1" style={{ background: fg }} />
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
                  {d.domain.replace(/_/g, " ")}
                </div>
                <div className="mt-1 text-lg font-semibold text-[#101828]">
                  {d.passed}/{total} <span className="text-[11px] text-[#667085]">passed</span>
                </div>
                <div className="text-[10px] text-[#667085] mt-1">
                  {d.failed} failed · {d.exception} exception · {d.not_started} not started
                </div>
                <div className="mt-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-1 rounded-full" style={{ width: `${pct}%`, background: fg }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Checks table */}
      {card && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-[#667085]">
              <tr>
                <th className="text-left px-3 py-2">Domain</th>
                <th className="text-left px-3 py-2">Sev.</th>
                <th className="text-left px-3 py-2">Check</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Attested by · at</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {card.checks.map((c) => {
                const st = STATUS_STYLE[c.status] || STATUS_STYLE.NOT_STARTED;
                return (
                  <tr key={c.id} className="hover:bg-slate-50"
                      data-testid={`readiness-check-${c.id}`}>
                    <td className="px-3 py-2 text-[11px] text-[#667085]">
                      {c.domain.replace(/_/g, " ")}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-mono font-bold text-[#101828]">
                        {c.severity}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-[13px] text-[#101828] font-medium">{c.title}</div>
                      <div className="text-[11px] text-[#667085]">{c.description}</div>
                      {c.note && <div className="text-[11px] text-[#667085] mt-0.5 italic">{c.note}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                            style={{ background: st.bg, color: st.fg }}
                            data-testid={`status-${c.id}`}>
                        {c.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-[#667085]">
                      {c.attested_by
                        ? <>{c.attested_by}<br />{c.attested_at?.slice(0, 16).replace("T", " ")}</>
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {c.probe ? (
                        <Button size="sm" variant="outline"
                                onClick={() => runProbe(c.id)}
                                data-testid={`probe-${c.id}`}
                                className="mr-1">
                          <PlayCircle className="h-3 w-3 mr-1" /> Probe
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline"
                              onClick={() => setAttest({ check: c })}
                              data-testid={`attest-${c.id}`}>
                        Attest
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sign-offs list */}
      {card?.signoffs?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4"
             data-testid="readiness-signoffs">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#667085] mb-2">
            Sign-offs
          </div>
          <ul className="space-y-1 text-[13px]">
            {card.signoffs.map((s) => (
              <li key={s.id} className="flex items-center gap-2">
                {s.decision === "APPROVED_FOR_PRODUCTION"
                  ? <CheckCircle2 className="h-4 w-4 text-[#169C70]" />
                  : s.decision === "NOT_APPROVED"
                  ? <XCircle className="h-4 w-4 text-[#C2333F]" />
                  : <AlertTriangle className="h-4 w-4 text-[#B25A00]" />}
                <span className="text-[#101828] font-medium">{s.role}</span>
                <span className="text-[#667085]">· {s.name}</span>
                <ChevronRight className="h-3 w-3 text-[#98A2B3]" />
                <span className="text-[#667085]">{s.decision.replace(/_/g, " ")}</span>
                {s.comments && <span className="text-[#667085] italic">— {s.comments}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <AttestDialog attest={attest} onClose={() => setAttest(null)}
                     tenantId={tenantId} onSaved={load} />
      <SignoffDialog open={signoffOpen} onClose={() => setSignoffOpen(false)}
                       tenantId={tenantId} onSaved={load} />
    </div>
  );
}

function AttestDialog({ attest, onClose, tenantId, onSaved }) {
  const [status, setStatus] = useState("PASSED");
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");
  const [exception, setException] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (attest) { setStatus("PASSED"); setNote(""); setEvidence(""); setException(""); }
  }, [attest]);
  if (!attest) return null;

  const save = async () => {
    setSaving(true);
    try {
      const params = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
      await api.post(`/production-readiness/attest${params}`, {
        check_id: attest.check.id, status, note,
        evidence_url: evidence || undefined,
        exception_reason: exception || undefined,
      });
      toast.success("Attestation recorded");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!attest} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="attest-dialog">
        <DialogHeader>
          <DialogTitle>{attest.check.title}</DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-[#667085]">{attest.check.description}</div>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
                    data-testid="attest-status"
                    className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
              {["PASSED", "PASSED_WITH_EXCEPTION", "FAILED", "BLOCKED",
                  "IN_PROGRESS", "RETEST_REQUIRED", "NOT_STARTED"].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Note</label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                       data-testid="attest-note" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Evidence URL</label>
            <Input value={evidence} onChange={(e) => setEvidence(e.target.value)}
                    placeholder="Runbook / test report / screenshot link"
                    data-testid="attest-evidence" />
          </div>
          {status === "PASSED_WITH_EXCEPTION" && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">
                Exception reason
              </label>
              <Textarea rows={2} value={exception}
                         onChange={(e) => setException(e.target.value)}
                         data-testid="attest-exception" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-brand-blue hover:bg-brand-blueh"
                    data-testid="attest-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Record attestation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignoffDialog({ open, onClose, tenantId, onSaved }) {
  const [role, setRole] = useState("PRODUCT");
  const [name, setName] = useState("");
  const [decision, setDecision] = useState("APPROVED_FOR_PRODUCTION");
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setRole("PRODUCT"); setName(""); setDecision("APPROVED_FOR_PRODUCTION"); setComments(""); }
  }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error("Sign-off name required"); return; }
    setSaving(true);
    try {
      await api.post("/production-readiness/signoff", {
        role, name, decision, comments, tenant_id: tenantId || undefined,
      });
      toast.success(`${role} sign-off recorded`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="signoff-dialog">
        <DialogHeader><DialogTitle>Go-live sign-off</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}
                      data-testid="signoff-role"
                      className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
                {["PRODUCT", "OPERATIONS", "FINANCE", "SECURITY",
                    "LEGAL_PRIVACY", "TECHNOLOGY", "CUSTOMER_PILOT_OWNER"].map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                      data-testid="signoff-name" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Decision</label>
            <select value={decision} onChange={(e) => setDecision(e.target.value)}
                    data-testid="signoff-decision"
                    className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1 bg-white">
              {["APPROVED_FOR_PRODUCTION", "CONDITIONALLY_APPROVED", "NOT_APPROVED"].map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#667085]">Comments</label>
            <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)}
                       data-testid="signoff-comments" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-brand-blue hover:bg-brand-blueh"
                    data-testid="signoff-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Record sign-off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
